import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      toast.success('Giriş uğurlu!');
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş xətası');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="relative flex items-center justify-center p-4 sm:p-8 bg-zinc-50">
        <div className="absolute bottom-3 left-0 right-0 text-center text-[11px] text-zinc-400 pointer-events-none" data-testid="login-version">
          v1.7.0
        </div>
        <div className="w-full max-w-md space-y-6 sm:space-y-8">
          <div className="text-center">
            <img 
              src="https://www.fiber.net.az/assets/uploads/2022/06/logo-04.png" 
              alt="Fibernet Logo" 
              className="h-16 sm:h-24 mx-auto mb-4"
              data-testid="login-logo"
            />
            <p className="mt-2 text-sm text-zinc-600">Enterprise Resource Planning</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" data-testid="login-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm font-semibold text-zinc-700">
                  İstifadəçi adı
                </Label>
                <div className="mt-1 relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" />
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 h-12 bg-white border-zinc-300 focus:border-primary focus:ring-primary"
                    placeholder="İstifadəçi adınızı daxil edin"
                    required
                    data-testid="username-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-zinc-700">
                  Şifrə
                </Label>
                <div className="mt-1 relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-white border-zinc-300 focus:border-primary focus:ring-primary"
                    placeholder="Şifrənizi daxil edin"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold uppercase tracking-wide"
              data-testid="login-submit-button"
            >
              {loading ? 'Gözləyin...' : 'Daxil ol'}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Image (hidden on mobile) */}
      <div 
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/flagged/photo-1580051720305-a944536881fb?w=1200')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-zinc-900/80"></div>
        <div className="relative h-full flex items-center justify-center text-white p-12">
          <div className="max-w-lg">
            <h2 className="text-5xl font-bold uppercase mb-4">Sürətli Xidmət</h2>
            <p className="text-xl leading-relaxed">Texniki xidmətlərinizi peşəkar şəkildə idarə edin</p>
          </div>
        </div>
      </div>
    </div>
  );
}
